import { useState } from "react";
import { QuizRunQuestion } from "@/components/courseCompletionQuiz/QuizRunQuestion";
import { Badge } from "@/components/ui/badge";
import { isWideQuiz } from "@/lib/codeQuiz";
import { needsAnswer } from "@/lib/courseCompletionQuiz";
import { cn } from "@/lib/utils";
import type {
  CompletionQuizQuestion,
  CompletionQuizRun,
} from "@/types/courseCompletionQuiz";

interface CourseCompletionQuizRunProps {
  /** The open run; the questions payload carries the same two counters. */
  run: Pick<CompletionQuizRun, "attemptNumber" | "totalCount">;
  maxAttempts: number;
  questions: CompletionQuizQuestion[];
  /** Sends one answer and resolves once it is stored. Rejects when that failed. */
  onSubmit: (
    question: CompletionQuizQuestion,
    type: string,
    userInput: string
  ) => Promise<void>;
  /** Every question is answered: on to the evaluation. */
  onFinish: () => void;
}

const keyOf = (question: CompletionQuizQuestion) =>
  `${question.contentElementId}:${question.entryIndex}`;

const firstOpenIndex = (questions: CompletionQuizQuestion[]): number => {
  for (const [position, question] of questions.entries()) {
    if (needsAnswer(question)) return position;
  }
  return Math.max(questions.length - 1, 0);
};

/** The next question that still has to be answered, or null when this was the last. */
const nextOpenIndex = (
  questions: CompletionQuizQuestion[],
  after: number
): number | null => {
  for (const [position, question] of questions.entries()) {
    if (position > after && needsAnswer(question)) return position;
  }
  return null;
};

const advanceLabelFor = (nextOpen: number | null) => {
  if (nextOpen === null) return "Zur Auswertung";
  return "Nächste Frage";
};

/** Current step and every step already behind it, drawn in the progress color. */
const stepInProgress = (
  question: CompletionQuizQuestion,
  position: number,
  index: number
) => position <= index || !needsAnswer(question);

/**
 * The questions of an open run, one at a time. Moving on stores the answer.
 * Whether it was right is not shown, because the server keeps the verdict
 * until the quiz is over. The progress strip stays full width for every
 * question. A code workspace fills the area under it; every other question
 * stays in the narrow column.
 */
export const CourseCompletionQuizRun = ({
  run,
  maxAttempts,
  questions,
  onSubmit,
  onFinish,
}: CourseCompletionQuizRunProps) => {
  const [index, setIndex] = useState(() => firstOpenIndex(questions));
  const [error, setError] = useState<string | null>(null);

  const question = questions[index];
  if (!question) return null;

  const total = run.totalCount || questions.length;
  const nextOpen = nextOpenIndex(questions, index);
  const advanceLabel = advanceLabelFor(nextOpen);

  const saveAnswer = async (type: string, userInput: string) => {
    setError(null);
    // The server keeps one submission. Sending a stored answer again is refused.
    if (!needsAnswer(question)) return;

    try {
      await onSubmit(question, type, userInput);
    } catch {
      setError(
        "Deine Antwort konnte nicht gespeichert werden. Bitte versuche es noch einmal."
      );
      throw new Error("not saved");
    }
  };

  const advance = () => {
    if (nextOpen === null) {
      onFinish();
      return;
    }
    setIndex(nextOpen);
  };

  const renderProgress = () => (
    <>
      <Badge className="mb-3">
        Versuch {run.attemptNumber} von {maxAttempts}
      </Badge>

      <h1 className="mb-3">
        Frage {index + 1} von {total}
      </h1>
      <ol className="mt-2 flex gap-1.5" aria-label="Fortschritt im Quiz">
        {questions.map((item, position) => (
          <li
            key={keyOf(item)}
            aria-current={position === index ? "step" : undefined}
            className={cn(
              "h-1.5 flex-1 rounded-full bg-(--progress-track)",
              stepInProgress(item, position, index) && "bg-secondary"
            )}
          />
        ))}
      </ol>
    </>
  );

  const renderNotices = () => {
    if (question.answer?.status === "failed" && !error) {
      return (
        <p role="status" className="mt-4 text-sm font-semibold text-primary">
          Die Auswertung deiner Antwort hat nicht geklappt. Bitte versuche es
          noch einmal.
        </p>
      );
    }
    if (error) {
      return (
        <p role="alert" className="mt-4 text-sm font-semibold text-destructive">
          {error}
        </p>
      );
    }
    return null;
  };

  const renderQuestion = () => (
    <QuizRunQuestion
      key={keyOf(question)}
      content={question.question}
      savedInput={question.answer?.userInput}
      advanceLabel={advanceLabel}
      onSubmit={saveAnswer}
      onAdvance={advance}
    />
  );

  const renderQuestionBody = () => {
    if (isWideQuiz(question.question.subType)) {
      return (
        <div className="min-h-0 flex-1 overflow-y-auto lg:overflow-hidden">
          {renderQuestion()}
        </div>
      );
    }

    return (
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-6 md:px-8">
          {renderQuestion()}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b bg-background px-4 py-4 md:px-8">
        {renderProgress()}
        {renderNotices()}
      </div>
      {renderQuestionBody()}
    </div>
  );
};
